import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, TablePagination, TableRow } from '@mui/material';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import NotFoundRecords from 'src/components/NotFoundRecords';
import GlobalInvoiceRow from './GlobalInvoiceRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ transactions, onView }) =>
  transactions.map((transaction) => (
    <GlobalInvoiceRow key={transaction.id} {...transaction} onView={onView} />
  ));

Tbody.propTypes = {
  transactions: PropTypes.array.isRequired,
  onView: PropTypes.func.isRequired
};

const GlobalInvoiceTable = ({
  className,
  transactions,
  count,
  page,
  handleChangePage,
  loading,
  onView,
  ...rest
}) => {
  const { t } = useTranslation();
  const headColumnWith = '9%';

  const theads = useMemo(() => () => (
    <TableRow component="tr">
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.DATE')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.INVOICE_MONTH')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.INVOICE')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.CLIENT_COLUMN')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.CONTRACT')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.CONCEPT_COLUMN')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.PAYMENT_STATUS_COLUMN')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.DAYS_OVERDUE')}
      </TableCell>
      <TableCell style={{ width: headColumnWith }} align="center">
        {t('TRANSACTION.BALANCE_COLUMN')}
      </TableCell>
      <TableCell style={{ width: '2%' }} align="center">
        <span
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0
          }}
        >
          {t('COMMON.ACTION')}
        </span>
      </TableCell>
    </TableRow>
  ));

  const tbody = useMemo(
    () => () => <Tbody transactions={transactions} onView={onView} />,
    [transactions, onView]
  );

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, transactions]
  );

  if (!loading && count === 0) {
    return (
      <Card>
        <NotFoundRecords
          title="NOT_FOUND_RECORDS_MESSAGE.TRANSACTION_TITLE"
          description="NOT_FOUND_RECORDS_MESSAGE.TRANSACTION_DESCRIPTION"
        />
      </Card>
    );
  }

  return (
    <Table
      {...rest}
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
      skeletonLoading
      skeletonColumns={10}
    />
  );
};

GlobalInvoiceTable.propTypes = {
  className: PropTypes.string,
  transactions: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool,
  onView: PropTypes.func.isRequired
};

GlobalInvoiceTable.defaultProps = {
  count: 0
};

export default GlobalInvoiceTable;
